import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  styled,
  Container,
  Modal,
  CircularProgress,
  Stack,
  Paper
} from '@mui/material';
import { toast } from 'react-toastify';

import LandingPageNav from '../../components/landingpage/LandingPageNav';
import Footer from '../../components/Footer/footer';

import { apigetPublic, apidelete } from '../../service/api';

// --- Modal for Editing ---
import StateEditModal from './Edit';

// -----------------------------
// Styled Components
// -----------------------------
const ProfileContainer = styled(Container)(() => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: 8,
  paddingTop: '84px',
  position: 'relative',
  zIndex: 1,
}));

const TopContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  marginBottom: theme.spacing(4),
  flexWrap: 'wrap',
}));

const StateInfo = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 300,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
  padding: theme.spacing(2),
  fontSize: '1rem',
  textTransform: 'none',
  backgroundColor: '#b79a59 !important',
  color: '#fff !important',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: '#a5894f !important',
  },
}));

// -----------------------------

export default function StateProfile() {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const [stateData, setStateData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [openEdit, setOpenEdit] = useState(false);

  // -----------------------------------
  // Determine whether ID is blank, slug, or MongoID
  // -----------------------------------
  const resolveStateIdentifier = () => {
  const raw = id?.trim();

  if (!raw) {
    const segments = window.location.pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    return { type: 'code', value: last.toUpperCase() };
  }

  // If it's a MongoId → treat as ID
  const isObjectId = /^[a-f0-9]{24}$/i.test(raw);
  if (isObjectId) return { type: 'id', value: raw };

  // Otherwise treat as a state code
  return { type: 'code', value: raw.toUpperCase() };
};


  // -----------------------------------
  // Fetch State by ID or Slug
  // -----------------------------------
  const fetchState = async () => {
    setLoading(true);

    try {
      const { type, value } = resolveStateIdentifier();

      let res;
      if (type === 'id') {
        res = await apigetPublic(`states/${value}`);
      } else {
        res = await apigetPublic(`states/code/${value}`);
      }

      if (res?.status === 200) {
        setStateData(res.data);
      } else {
        setStateData(null);
      }
    } catch (err) {
      toast.error('Failed to load state');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchState();
  }, [id]);

  // -----------------------------------
  // Delete State
  // -----------------------------------
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this state?')) return;

    try {
      const res = await apidelete(`states/${stateData._id}`);
      if (res.status === 200) {
        toast.success('State deleted');
        navigate('/dashboard/states');
      }
    } catch (err) {
      toast.error('Error deleting state');
    }
  };

  if (loading) {
    return (
      <Modal open>
        <Box sx={{
          position: 'absolute',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 300,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          textAlign: 'center'
        }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading State...</Typography>
        </Box>
      </Modal>
    );
  }

  if (!stateData) {
    return <Typography sx={{ mt: 10, textAlign: 'center' }}>State not found.</Typography>;
  }

  const canEdit = user?.role === 'admin' || user?.role === 'marketing';

  return (
    <>
      <LandingPageNav />

      <Box
        sx={{
          backgroundImage: 'url(https://res.cloudinary.com/ddhrdxc7d/image/upload/v1763995325/Background_Image_-_Blog_Pages_and_individual_LO_pages_1_w6e9ef.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'rgba(245,245,245,.85)',
          },
        }}
      >
        {/* Admin Buttons */}
        {canEdit && (
          <Box sx={{ p: 2 }}>
            <Button variant="outlined" onClick={() => setOpenEdit(true)}>
              Edit State
            </Button>

            <Button
              variant="outlined"
              color="error"
              onClick={handleDelete}
              sx={{ ml: 2 }}
            >
              Delete
            </Button>
          </Box>
        )}

        <ProfileContainer>

          {/* Top Section */}
          <TopContainer>
            {/* State Image */}
            <Box>
              <img
                src={stateData.branchImageUrl}
                alt={stateData.name}
                style={{
                  width: 260,
                  height: 200,
                  objectFit: 'cover',
                  borderRadius: 12,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                }}
              />
            </Box>

            {/* Info */}
            <StateInfo>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {stateData.name}
              </Typography>
              <Typography variant="h5" sx={{ color: 'text.secondary', mt: 1 }}>
                {stateData.code}
              </Typography>

              {/* Additional Options */}
              {stateData.additionalOptions?.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Additional Options
                  </Typography>
                  <ul>
                    {stateData.additionalOptions.map((opt) => (
                      <li key={opt}>
                        <Typography variant="body1">{opt}</Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
              )}

              {/* Branch List */}
              {stateData.branches?.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Branches in This State
                  </Typography>

                  {stateData.branches.map((b) => (
                    <Typography
                      key={b._id}
                      sx={{ mt: 1, color: '#1976d2', cursor: 'pointer' }}
                      onClick={() => navigate(`/branch/${encodeURIComponent(b.slug)}`)}
                    >
                      {b.name} — {b.city}, {b.stateCode}
                    </Typography>
                  ))}

                </Box>
              )}
            </StateInfo>
          </TopContainer>

          {/* Loan Officers */}
          <Box sx={{ mt: 5 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              Loan Officers Serving {stateData.name}
            </Typography>

            {stateData.loanOfficers && stateData.loanOfficers.length > 0 ? (
              stateData.loanOfficers.map((lo) => (
                <Paper
                  key={lo._id}
                  sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center' }}
                >
                  <img
                    src={lo.imageUrl}
                    alt={lo.firstName}
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      marginRight: 20,
                    }}
                  />

                  <Box>
                    <Typography variant="h6">
                      {lo.firstName} {lo.lastName}
                    </Typography>
                    <Typography>{lo.title}</Typography>

                    <Button
                      variant="text"
                      onClick={() => navigate(`/${lo.slug}`)}
                      sx={{ mt: 1 }}
                    >
                      View Profile
                    </Button>
                  </Box>
                </Paper>
              ))
            ) : (
              <Typography>No loan officers found for this state.</Typography>
            )}
          </Box>

        </ProfileContainer>
      </Box>

      {/* Edit Modal */}
      <StateEditModal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        stateData={stateData}
        onUpdate={fetchState}
      />

      <Footer />
    </>
  );
}
